import React from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';

import { CM_CHECKLIST_CATEGORIES } from './constants';
import type { ChecklistTabId } from './types';

interface Props {
  activeTabId: ChecklistTabId;
  areVisibleQueueItemsExpanded: boolean;
  isApprovalQueueMode: boolean;
  needsApprovalCount: number;
  searchQuery: string;
  showAutoApproved: boolean;
  visibleQueueItemCount: number;
  onExpandVisibleCards: () => void;
  onSearchQueryChange: (searchQuery: string) => void;
  onShowAutoApprovedChange: () => void;
  onTabChange: (tabId: ChecklistTabId) => void;
}

export default function ChecklistToolbar({
  activeTabId,
  areVisibleQueueItemsExpanded,
  isApprovalQueueMode,
  needsApprovalCount,
  searchQuery,
  showAutoApproved,
  visibleQueueItemCount,
  onExpandVisibleCards,
  onSearchQueryChange,
  onShowAutoApprovedChange,
  onTabChange,
}: Props) {
  const tabs = [
    { id: 'approvalQueue' as const, label: `Require approval (${needsApprovalCount})` },
    ...CM_CHECKLIST_CATEGORIES,
  ];

  return (
    <Stack spacing={1.5} alignItems="stretch">
      <Box
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1.25,
          display: 'flex',
          flexWrap: 'wrap',
          p: 0.5,
          width: '100%',
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <Button
              key={tab.id}
              color="inherit"
              size="small"
              variant={isActive ? 'contained' : 'text'}
              onClick={() => onTabChange(tab.id)}
              sx={{
                bgcolor: isActive ? 'background.paper' : 'transparent',
                boxShadow: isActive ? 1 : 'none',
                color: isActive ? 'text.primary' : 'text.secondary',
                fontWeight: isActive ? 700 : 500,
              }}
            >
              {tab.label}
            </Button>
          );
        })}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <TextField
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder="Search..."
          size="small"
          value={searchQuery}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220 }}
        />
        <Button
          disabled={!isApprovalQueueMode || visibleQueueItemCount === 0}
          size="small"
          variant="outlined"
          startIcon={areVisibleQueueItemsExpanded ? <ExpandLessIcon /> : <UnfoldMoreIcon />}
          onClick={onExpandVisibleCards}
        >
          {areVisibleQueueItemsExpanded ? 'Collapse details' : 'Expand details'}
        </Button>
        {!isApprovalQueueMode && (
          <Button
            size="small"
            variant="outlined"
            startIcon={showAutoApproved ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={onShowAutoApprovedChange}
          >
            {showAutoApproved ? 'Hide approved' : 'Show approved'}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
